import {
  CatalogItemGroupEntity,
  assetCatalogKeys,
  assetKeys,
  caseScheduleKeys,
  caseScheduleRepository,
  useAssetCatalogItems,
  useAssetsByCatalogIds,
  useAssetsByIds,
  useCase,
  useUnassignTrackedItem,
} from '@peritasai/repository';
import { AssetType, CaseBuildStatus, type AssetStatus } from '@peritasai/types';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DetectedAsset } from '../types/build-cart';
import { resolvePriorityStatusAsset } from '../utils/assetStatusLabels';
import { getCartActionLabel } from '../utils/cartAction';
import { useCaseAssignmentDiscovery } from './useCaseAssignmentDiscovery';

export type TraySource = 'committed' | 'candidate' | 'pending';

export type TrayCardViewModel = {
  checklistItemId: string;
  catalogItemId: string;
  name: string;
  productNumber: string;
  assetId: string | null;
  referenceNumber: string | null;
  source: TraySource;
  /** Current physical location, populated for committed/candidate trays. */
  locationDisplay: string | null;
  /** Best available location + count, populated for pending trays only. */
  availability: { locationName: string; count: number } | null;
  /** Real status for committed/candidate; priority-resolved status across
   * all units for pending (see ASSET_STATUS_PRIORITY_ORDER). */
  status: AssetStatus | null;
  /** Location of the specific unit behind `status`, for pending trays with
   * no AVAILABLE unit. */
  priorityAssetLocationDisplay: string | null;
};

/**
 * Correlates live-scanned candidate assets to open required-tray slots the
 * same way the backend's reserve-batch does (match by catalogItemId, first
 * open slot), so a tray moves from "pending" to "on cart" in this screen
 * before it is actually persisted via Save/Close — candidate slots are NOT
 * yet part of the CaseBuildChecklist.
 */
export const buildTrayCards = (
  requiredGroups: CatalogItemGroupEntity[],
  detectedAssets: DetectedAsset[],
  assetLocationById: Map<string, string>,
  catalogAvailabilityById: Map<string, { locationName: string; count: number }>,
  assetStatusById: Map<string, AssetStatus | null> = new Map(),
  pendingCatalogPriorityById: Map<
    string,
    { status: AssetStatus | null; locationDisplay: string | null }
  > = new Map()
): TrayCardViewModel[] => {
  const candidatesByCatalogId = new Map<string, DetectedAsset[]>();
  detectedAssets
    .filter((asset) => asset.assetType === AssetType.TRAY && asset.selected)
    .forEach((asset) => {
      const pool = candidatesByCatalogId.get(asset.catalogItemId) ?? [];
      pool.push(asset);
      candidatesByCatalogId.set(asset.catalogItemId, pool);
    });

  const committed: TrayCardViewModel[] = [];
  const candidate: TrayCardViewModel[] = [];
  const pending: TrayCardViewModel[] = [];

  for (const group of requiredGroups) {
    for (const item of group.items) {
      if (item.assetId) {
        committed.push({
          checklistItemId: item.checklistItemId,
          catalogItemId: group.catalogItemId,
          name: group.name,
          productNumber: group.productNumber,
          assetId: item.assetId,
          referenceNumber: null,
          source: 'committed',
          locationDisplay: assetLocationById.get(item.assetId) ?? null,
          availability: null,
          status: assetStatusById.get(item.assetId) ?? null,
          priorityAssetLocationDisplay: null,
        });
        continue;
      }

      const pool = candidatesByCatalogId.get(group.catalogItemId);
      const detected = pool?.shift();
      if (detected) {
        candidate.push({
          checklistItemId: item.checklistItemId,
          catalogItemId: group.catalogItemId,
          name: group.name,
          productNumber: group.productNumber,
          assetId: detected.id,
          referenceNumber: detected.subtitle,
          source: 'candidate',
          locationDisplay: detected.locationDisplay,
          availability: null,
          status: detected.status ?? null,
          priorityAssetLocationDisplay: null,
        });
        continue;
      }

      const priorityInfo = pendingCatalogPriorityById.get(group.catalogItemId) ?? null;
      pending.push({
        checklistItemId: item.checklistItemId,
        catalogItemId: group.catalogItemId,
        name: group.name,
        productNumber: group.productNumber,
        assetId: null,
        referenceNumber: null,
        source: 'pending',
        locationDisplay: null,
        availability: catalogAvailabilityById.get(group.catalogItemId) ?? null,
        status: priorityInfo?.status ?? null,
        priorityAssetLocationDisplay: priorityInfo?.locationDisplay ?? null,
      });
    }
  }

  // Figma frame shows on-cart trays first (top-left), ahead of pending.
  return [...committed, ...candidate, ...pending];
};

export type UseCaseCartDetailViewModelOptions = {
  caseId: string | null;
  /**
   * The picking station's location — android bridge location, or the
   * cart builder's manually-selected station (PVS-2593), resolved by the
   * caller the same way BuildCartPage resolves it. NOT the case's own
   * scheduled location (an OR), which is never where scanning happens.
   */
  locationId: string | null;
  /** Called once Save/Close has persisted and the discovery session has settled. */
  onReserveSettled: () => void;
  /** Called after a tray is successfully removed (committed or candidate). */
  onRemoveSuccess: () => void;
};

export const useCaseCartDetailViewModel = ({
  caseId,
  locationId,
  onReserveSettled,
  onRemoveSuccess,
}: UseCaseCartDetailViewModelOptions) => {
  const caseQuery = useCase(caseId, { enableRealtime: true });
  const caseEntity = caseQuery.data;
  const [isReserveSuccess, setIsReserveSuccess] = useState(false);
  const queryClient = useQueryClient();

  // Force a refetch on every entry rather than trusting the cache, since a
  // prior visit to this or another case may have left it stale.
  useEffect(() => {
    if (!caseId) return;
    void queryClient.invalidateQueries({ queryKey: caseScheduleKeys.detail(caseId) });
  }, [caseId, queryClient]);

  const requiredGroups = useMemo(
    () => caseEntity?.buildChecklist?.getTrays() ?? [],
    [caseEntity]
  );

  const committedAssetIds = useMemo(
    () =>
      requiredGroups.flatMap((group) =>
        group.items
          .map((item) => item.assetId)
          .filter((assetId): assetId is string => !!assetId)
      ),
    [requiredGroups]
  );

  // Already-committed trays (and the case's own assigned cart, if any)
  // shouldn't also show up as newly-detected candidates — same pattern as
  // BuildCartPage's excludedAssetIds.
  const excludedAssetIds = useMemo(() => {
    const selectedCartAssetId = caseEntity?.selectedCart?.assetId;
    return selectedCartAssetId
      ? [...committedAssetIds, selectedCartAssetId]
      : committedAssetIds;
  }, [committedAssetIds, caseEntity?.selectedCart?.assetId]);

  const discovery = useCaseAssignmentDiscovery({
    enabled: !caseQuery.isLoading && !caseQuery.isError,
    isSuccess: isReserveSuccess,
    locationId,
    caseId,
    excludedAssetIds,
    onHubOffline: () => undefined,
    onReserveSuccess: () => setIsReserveSuccess(true),
    onReserveSettled: () => {
      setIsReserveSuccess(false);
      void caseQuery.refetch();
      onReserveSettled();
    },
    onCancel: () => undefined,
  });

  const assetQueries = useAssetsByIds(committedAssetIds);
  // useQueries returns a new array reference every render — key on
  // dataUpdatedAt so the memo below only recomputes when data actually
  // changes (same pattern as BuildCartPage's onCartAssetDataKey).
  const assetDataKey = assetQueries.map((q) => q.dataUpdatedAt ?? 0).join(',');
  const assetLocationById = useMemo(() => {
    const map = new Map<string, string>();
    assetQueries.forEach((query, index) => {
      const assetId = committedAssetIds[index];
      if (assetId && query.data) {
        map.set(assetId, query.data.getLocationDisplay());
      }
    });
    return map;

  }, [committedAssetIds, assetDataKey]);

  const assetStatusById = useMemo(() => {
    const map = new Map<string, AssetStatus | null>();
    assetQueries.forEach((query, index) => {
      const assetId = committedAssetIds[index];
      if (assetId && query.data) {
        map.set(assetId, query.data.status);
      }
    });
    return map;

  }, [committedAssetIds, assetDataKey]);

  const pendingCatalogItemIds = useMemo(
    () =>
      requiredGroups
        .filter((group) => group.items.some((item) => !item.assetId))
        .map((group) => group.catalogItemId),
    [requiredGroups]
  );

  // assetKeys/assetCatalogKeys are cached globally, shared across cases —
  // force one refetch whenever the relevant id set changes. Keyed on the ids
  // themselves (not just caseId): on mount, caseEntity can be stale cached
  // data while the case-detail refetch above is still in flight, so this can
  // first run with a stale checklist; keying purely on caseId would then
  // never re-invalidate once the refetch resolves with different ids.
  const invalidatedForIdsKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!caseId || !caseEntity) return;
    const idsKey = `${caseId}::${committedAssetIds.join(',')}::${pendingCatalogItemIds.join(',')}`;
    if (invalidatedForIdsKeyRef.current === idsKey) return;
    invalidatedForIdsKeyRef.current = idsKey;
    committedAssetIds.forEach((assetId) => {
      void queryClient.invalidateQueries({ queryKey: assetKeys.item(assetId) });
    });
    pendingCatalogItemIds.forEach((catalogItemId) => {
      void queryClient.invalidateQueries({
        queryKey: assetCatalogKeys.item(catalogItemId),
      });
      void queryClient.invalidateQueries({
        queryKey: assetKeys.list({ assetCatalogId: catalogItemId }),
      });
    });
  }, [caseId, caseEntity, committedAssetIds, pendingCatalogItemIds, queryClient]);

  const catalogQueries = useAssetCatalogItems(pendingCatalogItemIds);
  const catalogDataKey = catalogQueries.map((q) => q.dataUpdatedAt ?? 0).join(',');
  const catalogAvailabilityById = useMemo(() => {
    const map = new Map<string, { locationName: string; count: number }>();
    catalogQueries.forEach((query, index) => {
      const catalogItemId = pendingCatalogItemIds[index];
      const bestLocation = query.data?.bestAvailableLocation;
      if (catalogItemId && bestLocation) {
        map.set(catalogItemId, {
          locationName: bestLocation.locationName,
          count: bestLocation.count,
        });
      }
    });
    return map;

  }, [pendingCatalogItemIds, catalogDataKey]);

  const pendingCatalogAssetQueries = useAssetsByCatalogIds(pendingCatalogItemIds);
  const pendingCatalogAssetDataKey = pendingCatalogAssetQueries
    .map((q) => q.dataUpdatedAt ?? 0)
    .join(',');
  const pendingCatalogPriorityById = useMemo(() => {
    const map = new Map<
      string,
      { status: AssetStatus | null; locationDisplay: string | null }
    >();
    pendingCatalogAssetQueries.forEach((query, index) => {
      const catalogItemId = pendingCatalogItemIds[index];
      if (catalogItemId && query.data) {
        const priorityAsset = resolvePriorityStatusAsset(query.data);
        const locationDisplay = priorityAsset?.getLocationDisplay() ?? null;
        map.set(catalogItemId, {
          status: priorityAsset?.status ?? null,
          // getLocationDisplay() returns 'N/A', not null, when there's no location.
          locationDisplay:
            locationDisplay && locationDisplay !== 'N/A' ? locationDisplay : null,
        });
      }
    });
    return map;
  }, [pendingCatalogItemIds, pendingCatalogAssetDataKey]);

  const trayCards = useMemo(
    () =>
      buildTrayCards(
        requiredGroups,
        discovery.detectedAssets,
        assetLocationById,
        catalogAvailabilityById,
        assetStatusById,
        pendingCatalogPriorityById
      ),
    [
      requiredGroups,
      discovery.detectedAssets,
      assetLocationById,
      catalogAvailabilityById,
      assetStatusById,
      pendingCatalogPriorityById,
    ]
  );

  const onCartCount = useMemo(
    () => trayCards.filter((card) => card.source !== 'pending').length,
    [trayCards]
  );
  const pendingCount = trayCards.length - onCartCount;
  const isComplete = trayCards.length > 0 && pendingCount === 0;

  const firstDetectedCart = discovery.detectedAssets.find(
    (asset) => asset.assetType === AssetType.CASE_CART && asset.selected
  );
  const cartReferenceNumber =
    caseEntity?.selectedCart?.referenceNumber ?? firstDetectedCart?.subtitle ?? null;
  // Never overridden by a later live detection once a cart is saved.
  const cartAssetIdToReserve = caseEntity?.selectedCart?.assetId ?? firstDetectedCart?.id;

  // Every "On Cart" tray (committed + candidate), not just this session's
  // newly-detected candidates, so an already-committed tray set is
  // re-associated with a cart arriving later via SSE.
  const onCartTrayAssetIds = useMemo(
    () =>
      trayCards
        .filter((card) => card.source !== 'pending' && card.assetId)
        .map((card) => card.assetId as string),
    [trayCards]
  );

  const handleSave = useCallback(
    () => discovery.handleReserve(onCartTrayAssetIds, cartAssetIdToReserve),
    [discovery, onCartTrayAssetIds, cartAssetIdToReserve]
  );

  // A different cart detected after one is already saved must not enable
  // Save either — cartAssetIdToReserve above won't send it anyway, so that
  // click would silently resend the old cart id.
  const hasNewCandidateTray = trayCards.some((card) => card.source === 'candidate');
  const hasNewCartDetection = !caseEntity?.selectedCart?.assetId && !!firstDetectedCart;
  const hasUnsavedChanges = hasNewCandidateTray || hasNewCartDetection;

  const unassignMutation = useUnassignTrackedItem();

  const handleRemoveConfirmed = useCallback(
    (card: TrayCardViewModel) => {
      if (card.source === 'candidate' && card.assetId) {
        discovery.handleRemoveDetectedAsset(card.assetId);
        onRemoveSuccess();
        return;
      }
      if (card.source === 'committed' && card.assetId && caseId) {
        // Remove locally too, or a still-detecting hub would immediately
        // re-match it as a 'candidate' once the unassign succeeds.
        discovery.handleRemoveDetectedAsset(card.assetId);
        unassignMutation.mutate(
          { caseId, assetId: card.assetId },
          {
            onSuccess: async (updatedCase) => {
              // Symmetric with the reserve-side sync in
              // useCaseAssignmentDiscovery: a removal can take a previously
              // fully-covered build back below its required tray count, so
              // buildStatus must be pulled back down too — otherwise it goes
              // stale at COMPLETE and a later dispatch would incorrectly
              // resolve to "Dispatched" instead of "Dispatched - Incomplete".
              const checklist = updatedCase.buildChecklist;
              if (checklist) {
                const isFullyCovered =
                  getCartActionLabel(
                    checklist.traysAllocated,
                    checklist.traysReserved,
                    checklist.traysTotal
                  ) === 'dispatch';
                if (!isFullyCovered) {
                  try {
                    await caseScheduleRepository.updateBuildStatus(
                      caseId,
                      CaseBuildStatus.INCOMPLETE
                    );
                  } catch (error) {
                    // Don't let a failure here mask the removal that already succeeded.
                    console.error('Failed to sync buildStatus after remove', error);
                  }
                }
              }
              onRemoveSuccess();
            },
          }
        );
      }
    },
    [discovery, unassignMutation, caseId, onRemoveSuccess]
  );

  return {
    caseEntity,
    isLoading: caseQuery.isLoading,
    isError: caseQuery.isError,
    refetch: caseQuery.refetch,
    trayCards,
    onCartCount,
    pendingCount,
    isComplete,
    cartReferenceNumber,
    hasUnsavedChanges,
    handleSave,
    handleRemoveConfirmed,
    isRemoving: unassignMutation.isPending,
  };
};
